import { billingApi } from '@/api/billingApi'
import { organizationsApi } from '@/api/organizationsApi'
import type { ConsoleUser } from '@/stores/auth'
import type { BillingStatus, Plan, UpgradeResponse } from '@/types/subscription'
import { isPlatformDeployment } from '@/utils/deployment'
import { useMemo } from 'react'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'

interface SubscriptionState {
	plans: Plan[]
	currentBillingStatus: BillingStatus | null
	loading: boolean
	error: string | null
	organizationId: string | null
}

interface SubscriptionActions {
	fetchPlans: (organizationId?: string | null) => Promise<void>
	fetchBillingStatus: (organizationId?: string | null) => Promise<void>
	processSubscription: (
		organizationId: string,
		planId: string,
		period: 'Yearly' | 'Monthly',
	) => Promise<UpgradeResponse>
	refreshBillingStatus: () => Promise<void>
	setOrganizationId: (organizationId: string | null) => void
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
	clearError: () => void
}

type SubscriptionStore = SubscriptionState & SubscriptionActions

const initialState: SubscriptionState = {
	plans: [],
	currentBillingStatus: null,
	loading: false,
	error: null,
	organizationId: null,
}

export const useSubscriptionStore = create<SubscriptionStore>()(
	devtools(
		persist(
			subscribeWithSelector((set, get) => ({
				...initialState,

				fetchPlans: async (organizationId?: string | null) => {
					set({ loading: true, error: null })
					try {
						const targetOrgId = organizationId || get().organizationId
						if (!targetOrgId) {
							// No organization ID available - use public plans as fallback
							// This is common for new users on console deployment who haven't created an organization yet
							const plans = await billingApi.getPublicPlans()
							set({ plans, loading: false })
							return
						}
						const plans = await billingApi.getPlans(targetOrgId)
						set({ plans, loading: false })
					} catch (err) {
						set({ error: (err as Error).message, loading: false })
						throw err
					}
				},

				fetchBillingStatus: async (organizationId?: string | null) => {
					set({ loading: true, error: null })
					try {
						if (isPlatformDeployment()) {
							set({ currentBillingStatus: null, loading: false })
							return
						}

						const user = await billingApi.getCurrentUserWithSubscription()
						const targetOrgId = organizationId || get().organizationId || user.organizations?.[0]?.id || null
						const billingStatus = billingApi.getBillingStatusFromUser(user as ConsoleUser, targetOrgId)

						if (billingStatus) {
							const selectedOrgId = targetOrgId || user.organizations?.[0]?.id || null
							set({
								currentBillingStatus: billingStatus,
								organizationId: selectedOrgId,
								loading: false,
							})
						} else {
							set({ currentBillingStatus: null, loading: false })
						}
					} catch (err) {
						set({ error: (err as Error).message, loading: false })
					}
				},

				processSubscription: async (
					organizationId: string,
					planId: string,
					period: 'Yearly' | 'Monthly',
				): Promise<UpgradeResponse> => {
					set({ loading: true, error: null })
					try {
						const currentState = get()
						let billingStatus = currentState.currentBillingStatus

						// If the target organizationId doesn't match the cached organizationId,
						// we need to fetch fresh billing status for the correct organization
						// to determine if this is a first-time subscription or a modification
						if (currentState.organizationId !== organizationId) {
							const user = await billingApi.getCurrentUserWithSubscription()
							billingStatus = billingApi.getBillingStatusFromUser(user as ConsoleUser, organizationId)
						}

						// Use subscribe for initial subscription (pending_initial_payment or no subscription)
						// Use modify for upgrading existing subscriptions
						const isFirstTimeSubscription =
							!billingStatus || billingStatus.subscription_state === 'pending_initial_payment'

						const response = isFirstTimeSubscription
							? await organizationsApi.subscribeOrganization(organizationId, planId, period)
							: await organizationsApi.modifySubscription(organizationId, planId, period)

						set({ loading: false })
						return response
					} catch (err) {
						set({ error: (err as Error).message, loading: false })
						throw err
					}
				},

				refreshBillingStatus: async () => {
					await get().fetchBillingStatus()
				},

				setOrganizationId: (organizationId: string | null) => set({ organizationId }),

				setLoading: (loading: boolean) => set({ loading }),

				setError: (error: string | null) => set({ error }),

				clearError: () => set({ error: null }),
			})),
			{
				name: 'subscription-storage',
				partialize: state => ({
					plans: state.plans,
					currentBillingStatus: state.currentBillingStatus,
					organizationId: state.organizationId,
				}),
			},
		),
		{
			name: 'subscription-store',
		},
	),
)

// Selectors
export const usePlans = () => useSubscriptionStore(state => state.plans)
export const useBillingStatus = () => useSubscriptionStore(state => state.currentBillingStatus)
export const useSubscriptionLoading = () => useSubscriptionStore(state => state.loading)
export const useSubscriptionError = () => useSubscriptionStore(state => state.error)
export const useSubscriptionOrganizationId = () => useSubscriptionStore(state => state.organizationId)

export const useSubscriptionActions = () => {
	const fetchPlans = useSubscriptionStore(state => state.fetchPlans)
	const fetchBillingStatus = useSubscriptionStore(state => state.fetchBillingStatus)
	const processSubscription = useSubscriptionStore(state => state.processSubscription)
	const refreshBillingStatus = useSubscriptionStore(state => state.refreshBillingStatus)
	const setOrganizationId = useSubscriptionStore(state => state.setOrganizationId)
	const setLoading = useSubscriptionStore(state => state.setLoading)
	const setError = useSubscriptionStore(state => state.setError)
	const clearError = useSubscriptionStore(state => state.clearError)

	return useMemo(
		() => ({
			fetchPlans,
			fetchBillingStatus,
			processSubscription,
			refreshBillingStatus,
			setOrganizationId,
			setLoading,
			setError,
			clearError,
		}),
		[
			fetchPlans,
			fetchBillingStatus,
			processSubscription,
			refreshBillingStatus,
			setOrganizationId,
			setLoading,
			setError,
			clearError,
		],
	)
}
